import React, { useContext, useState } from 'react';
import { Empty, TabsProps } from 'antd';

import Auth0RolesPanel from '@totem/components/administration/auth0/roles/panel/Auth0RolesPanel';
import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import ComplianceSelector from '@totem/components/controlSystemDetail/ComplianceSelector';
import ControlSystemContext from '@totem/components/controlSystemDetail/container/ControlSystemContext';
import ControlSystemSidebar from '@totem/components/controlSystemDetail/controlSystemSidebar/ControlSystemSidebar';
import ControlSystemKeysContainer from '@totem/components/controlSystemDetail/keys/ControlSystemKeysContainer';
import ControlSystemKeysPanel from '@totem/components/controlSystemDetail/keys/ControlSystemKeysPanel';
import DevicesContainer, {
  containerConfiguration as devicesContainerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import DocumentsContainer from '@totem/components/documents/report/documentsContainer';
import DocumentsPanel from '@totem/components/documents/report/documentsPanel';
import DeviceTable from '@totem/components/portfolio/buildings/details/DeviceTable';
import DeviceBackupPanel from '@totem/components/services/backup/deviceBackups/DeviceBackupPanel';
import EventsContainer, {
  containerConfiguration as eventContainerConfiguration,
} from '@totem/components/services/threats/eventsContainer/EventsContainer';
import EventsTable from '@totem/components/services/threats/eventsContainer/EventsTable';
import SupportLineAddButton from '@totem/components/supportLines/supportLineAddButton';
import SupportLinesContainer from '@totem/components/supportLines/supportLinesContainer';
import SupportLinesRecordCount from '@totem/components/supportLines/supportLinesRecordCount';
import SupportLinesTable from '@totem/components/supportLines/supportLinesTable';
import ComplianceSummaryContainer from '@totem/components/surveyV2/complianceSummary/complianceSummaryContainer';
import PolicyAuditReportContainer from '@totem/components/surveyV2/policyAuditReport/policyAuditReportContainer';
import ReportTable from '@totem/components/surveyV2/policyAuditReport/ReportTable';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SecurityGroupCheckConstraints } from '@totem/types/user';
import { isNotNull, isNull } from '@totem/utilities/common';

import './controlSystemDetail.css';

const ControlSystemDetailContent = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { data } = useContext(ControlSystemContext);
  const [deviceTotal, setDeviceTotal] = useState<number>(0);
  const [eventTotal, setEventTotal] = useState<number>(0);

  const id = isNotNull(data) ? data.controlSystem.id : emptyId;

  const securityCheckConstraints: SecurityGroupCheckConstraints = {
    regionId:
      isNotNull(data) && isNotNull(data.region) ? data.region.id : emptyId,
    buildingId:
      isNotNull(data) && isNotNull(data.building) ? data.building.id : emptyId,
    controlSystemId:
      isNotNull(data) && isNotNull(data.controlSystem)
        ? data.controlSystem.id
        : emptyId,
  };

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: '1',
        label: 'Compliance',
        children: (
          <ComplianceSummaryContainer controlSystemId={[id]}>
            <ComplianceSelector />
          </ComplianceSummaryContainer>
        ),
      },
      {
        key: '4',
        label: 'Continuity',
        children: (
          <DeviceBackupPanel
            pRegionId={[]}
            pBuildingId={[]}
            pControlSystemId={[id]}
            pDeviceId={[]}
            componentKey={'control-system.deviceBackups'}
          />
        ),
      },
      {
        key: 'devices',
        label: 'Devices',
        children: (
          <InputContainer
            filterConfig={devicesContainerConfiguration}
            defaultSort={{
              field: 'device.displayName',
              direction: 'ascend',
            }}
            staticFilters={[{ field: 'controlSystemId', values: [id] }]}
          >
            <DevicesContainer
              onRecordTotalChanged={(total) => setDeviceTotal(total)}
            >
              <IBCard
                isCovered
                title={'Devices'}
                extra={<RecordCountBadge count={deviceTotal} />}
              >
                <DeviceTable />
              </IBCard>
            </DevicesContainer>
          </InputContainer>
        ),
      },
    ];

    if (
      (permissions.systemAdministrator.hasPermissions(userProfile),
      securityCheckConstraints)
    ) {
      tabItems.push({
        key: 'audits',
        label: (
          <IBTooltip content={permissions.systemAdministrator.tooltip}>
            <span>Audits</span>
          </IBTooltip>
        ),
        children: (
          <PolicyAuditReportContainer staticFilters={{ controlSystemId: [id] }}>
            <IBCard isCovered title={'Policy Audits'}>
              <ReportTable />
            </IBCard>
          </PolicyAuditReportContainer>
        ),
      });
    }

    if (
      permissions.events.hasPermissions(userProfile, securityCheckConstraints)
    ) {
      tabItems.push({
        key: '2',
        label: (
          <IBTooltip content={permissions.events.tooltip}>
            <span>Events</span>
          </IBTooltip>
        ),
        children: (
          <InputContainer
            componentKey={'control-system.events'}
            componentLabel={'Control System: Events'}
            filterConfig={eventContainerConfiguration}
            defaultSort={{
              field: 'eventTime',
              direction: 'descend',
            }}
            staticFilters={[{ field: 'controlSystem', values: [id] }]}
          >
            <EventsContainer
              onRecordTotalChanged={(total) => setEventTotal(total)}
            >
              <IBCard
                isCovered
                title={'Events'}
                extra={<RecordCountBadge count={eventTotal} />}
              >
                <EventsTable />
              </IBCard>
            </EventsContainer>
          </InputContainer>
        ),
      });
    }

    if (
      permissions.remoteAccess.hasPermissions(
        userProfile,
        securityCheckConstraints,
      )
    ) {
      tabItems.push({
        key: 'remoteAccess',
        label: (
          <IBTooltip content={permissions.remoteAccess.tooltip}>
            Remote Access
          </IBTooltip>
        ),
        children: (
          <Auth0RolesPanel
            controlSystemId={data.controlSystem.id}
            componentKey={'control-system.remoteAccessRoles'}
          />
        ),
      });
    }

    tabItems.push({
      key: 'documents',
      label: 'Documents',
      children: (
        <DocumentsContainer
          documentReference={{
            controlSystemId: data.controlSystem.id,
            regionId: data.controlSystem.regionId,
            buildingId: data.controlSystem.buildingId,
            objectReference: {
              id: data.controlSystem.id,
              name: data.controlSystem.name,
              type: 'Control System',
              description: data.controlSystem.name,
              createdBy: '',
              createdAt: '',
            },
          }}
        >
          <div>
            <DocumentsPanel />
          </div>
        </DocumentsContainer>
      ),
    });

    tabItems.push({
      key: 'supportLines',
      label: 'Third Party Support',
      children: (
        <SupportLinesContainer
          owner={{
            id: EMPTY_ID,
            organizationId: userProfile.organization.id,
            regionId: data.controlSystem.regionId,
            buildingId: data.controlSystem.buildingId,
            controlSystemId: data.controlSystem.id,
            objectType: 'controlSystem',
            objectId: data.controlSystem.id,
            objectName: data.controlSystem.name,
            supportLines: [],
          }}
        >
          <IBCard
            isCovered
            title={'Third Party Support'}
            extra={
              <>
                <SupportLinesRecordCount />
                &nbsp;&nbsp;&nbsp;
                <SupportLineAddButton />
              </>
            }
          >
            <SupportLinesTable />
          </IBCard>
        </SupportLinesContainer>
      ),
    });

    if (permissions.internalSystemAdministrator.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'keys',
        label: (
          <IBTooltip content={permissions.internalSystemAdministrator.tooltip}>
            Keys
          </IBTooltip>
        ),
        children: (
          <ControlSystemKeysContainer controlSystemId={id}>
            <ControlSystemKeysPanel />
          </ControlSystemKeysContainer>
        ),
      });
    }

    return tabItems;
  };

  return (
    <>
      {isNull(data) && <Empty />}
      {isNotNull(data) && (
        <ContentLayout
          pageTitle={isNotNull(data) ? data.controlSystem.name : ''}
          contentStyle={{ padding: 0 }}
          pageSubtitle={[
            { label: userProfile.organization.name },
            { label: isNotNull(data) ? data.region.name : '' },
            {
              label: isNotNull(data) ? data.building.name : '',
              link: `/dashboard/buildings/${isNotNull(data) ? data.building.id : emptyId}`,
            },
          ]}
          breadcrumbs={(breadcrumbs) => [
            ...breadcrumbs,
            {
              label: isNotNull(data) ? data.building.name : '',
              link: `/dashboard/buildings/${isNotNull(data) ? data.building.id : emptyId}`,
            },
            {
              label: 'Control Systems',
              link: '/dashboard/controlSystemReport',
            },
            {
              label: 'System Details',
            },
          ]}
        >
          <div styleName="content">
            <div styleName="base-pane">
              <ControlSystemSidebar />
              <div styleName="main">
                <IBTabs defaultActiveKey="1" items={getTabItems()} />
              </div>
            </div>
          </div>
        </ContentLayout>
      )}
    </>
  );
};

export default ControlSystemDetailContent;
